import { Fragment, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { PulseIndicator, TextField } from 'basic-components';
import { DataStorageQuery } from 'dashboard-services/api/dataStorage';
import Query from 'dashboard-services/api/Query';

import getApiConfig from 'api/ApiConfig';

import MessageContainer from '../MessageContainer';
import Icon from './Icon';

import { useMutation } from '@tanstack/react-query';
import { When } from 'react-if';

const useValidateNcsv = ({ getQueryBuilder }) =>
  useMutation({
    ...DataStorageQuery.QUERIES.validateNCSV(
      getQueryBuilder
    )
  })

const ValidateStep = ({
  defaultName,
  setName,
  setErrorCells,
  setIsError,
  setIsSucess,
  isError,
  withIcon = false,
  worksheetRef,
  ref
}) => {
  const dispatch = useDispatch(),
        [isValid, setIsValid] = useState(),
        [errorMessage, setErrorMessage] = useState(undefined),
        [warningMessage, setWarningMessage] = useState(undefined),
        textFieldRef = useRef(),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withShouldDisplayError(false)
            .withOnError(({ response = [], status }) => {
              const fullResponse = response.slice()
              const withoutLimitRow = fullResponse.length > 1 ? fullResponse.slice(0,-1) : fullResponse
              isError(withoutLimitRow)
              setErrorCells(withoutLimitRow.map(row => Object.assign({}, row, { rowNumber: row?.rowNumber - 1 })))
              if(status === 413) {
                setWarningMessage("NSCV file validation doesn't support files with more than 1000 rows. You can proceed without validation.")
                setIsValid(true)
                setIsError(false)
                setIsSucess(true)
                return
              } else if(withoutLimitRow.some(e => e?.rowNumber === -1 && e?.columnNumber === -1)) {
                setErrorMessage(response.filter(obj => obj.rowNumber !== undefined)?.[0]?.message)
              } else {
                setErrorMessage("Issues were found in highlighted cells below")
              }
              setIsValid(false)
              setIsError(true)
              setIsSucess(false)
            })
            .withFetching(false)
        , [dispatch, isError, setErrorCells, setIsError, setIsSucess]),
        { mutateAsync: validateNcsvCall, isPending: isValidating } = useValidateNcsv({ getQueryBuilder }),
        validateNCSV = useCallback(() => 
          validateNcsvCall({ content: worksheetRef.current?.getAsCSV() })
            .then(() => {
              setIsValid(true)
              setIsError(false)
              setIsSucess(true)
            })
        , [setIsError, setIsSucess, validateNcsvCall, worksheetRef])

  useEffect(() => {
    validateNCSV()
  }, [validateNCSV])

  useImperativeHandle(ref, () => ({
    validateForForm: () => textFieldRef.current.validateForForm(),
    isValid: () => isValid && textFieldRef.current?.isValid
  }))

  return (
    <Fragment>
      <When condition={withIcon}>
        <Icon
            errorMessage={errorMessage}
            isValidating={isValidating}
            warningMessage={warningMessage}
        />
      </When>
      <div className="ncsv-creator__top__step ncsv-creator__top__step--file-name">
        <div className="ncsv-creator__top__step__row ncsv-creator__top__step__row--value">
          <TextField
              defaultValue={defaultName}
              name="name"
              noError
              onChange={setName}
              ref={textFieldRef}
              required
          />
        </div>
        <When condition={isValidating}>
          {() => (
            <div className="ncsv-creator__top__step__row ncsv-creator__top__step__row--label">
              <div className="ncsv-creator__top__step__row__label__text">The file is being validated</div>
              <div>
                <PulseIndicator 
                    loading
                    size={8}
                />
              </div>
            </div>
          )}
        </When>
        <When condition={!isValidating}>
          {() => (
            <MessageContainer
                errorMessage={errorMessage}
                successMessage={isValid ? "NCSV file configuration went succesfully!" : ""}
                warningMessage={warningMessage}
            />
          )}
        </When>
      </div>
    </Fragment>
  )
}

ValidateStep.displayName = "ValidateStep"


ValidateStep.propTypes = {
  defaultName: PropTypes.string.isRequired,
  isError: PropTypes.func.isRequired,
  setErrorCells: PropTypes.func.isRequired,
  setIsError: PropTypes.func.isRequired,
  setIsSucess: PropTypes.func.isRequired,
  setName: PropTypes.func.isRequired,
  withIcon: PropTypes.bool,
  worksheetRef: PropTypes.object.isRequired,
}

export default ValidateStep